import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Skeleton,
  Space,
  theme,
  Typography,
} from 'antd';
import { ChevronDown, CircleMinus } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { catchError, EMPTY, of, switchMap, tap } from 'rxjs';
import SyncSuspense from '../../../components/sync-suspense';
import { Ingredient, MealItem, MealItemIngredient, Measure, ServingTool } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { randomUUID } from '../../../utils/utils';

const REQUIRED_SERVING_MEASURE_MESSAGE = 'Please select a serving measure';
const REQUIRED_MEASURE_MESSAGE = 'Please select a measure';
const SUCCESS_ADD_MESSAGE = 'MealItem added successfully';
const SUCCESS_UPDATE_MESSAGE = 'MealItem updated successfully';

interface MealItemFormProps {
  mealItem: Partial<MealItem>;
  onSuccess: (mealItem: MealItem, newMealItem: boolean) => void;
  onCancel: () => void;
}

const IngredientsListContext = createContext<Ingredient[]>([]);
const MeasuresListContext = createContext<Measure[]>([]);

export const MealItemIngredientSelector = ({
  fieldIdx,
  onRemove,
}: {
  fieldIdx: number;
  onRemove: () => void;
}): ReactNode => {
  const { token } = theme.useToken();
  const ingredientsList = useContext(IngredientsListContext);
  const measuresList = useContext(MeasuresListContext);
  const isPrimary = Form.useWatch(['mealItemIngredients', fieldIdx, 'isPrimary']);

  return (
    <Flex justify="space-around" gap="small">
      <Form.Item name={[fieldIdx, 'id']} hidden>
        <Input />
      </Form.Item>

      <Form.Item style={{ maxHeight: 32 }}>
        <Flex justify="space-around" gap="small">
          <Button
            shape="circle"
            size="small"
            type="text"
            icon={<CircleMinus />}
            onClick={onRemove}
            danger
            disabled={isPrimary}
          />
        </Flex>
      </Form.Item>

      <Badge.Ribbon
        text={isPrimary ? 'Primary' : ''}
        color={token.colorPrimary}
        style={{ marginBlockStart: -token.marginLG }}
      >
        <Space.Compact style={{ flex: '1 1 100%' }}>
          <Form.Item
            name={[fieldIdx, 'ingredient', 'id']}
            style={{ marginBlock: 0, flex: '0 1 1000px' }}
            rules={[{ required: true, message: 'Select an ingredient' }]}
          >
            <Select
              optionFilterProp="name"
              showSearch
              options={ingredientsList ?? []}
              fieldNames={{ label: 'name', value: 'id' }}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name={[fieldIdx, 'units']}
            rules={[{ type: 'number', min: 0.1, message: 'Provide a positive number of units' }]}
            style={{ flex: '0 0 70px' }}
          >
            <InputNumber precision={2} style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item
            name={[fieldIdx, 'measure', 'id']}
            rules={[{ required: true, message: 'Provide a valid measure' }]}
            style={{ flex: '0 0 100px' }}
          >
            <Select
              showSearch
              optionFilterProp="name"
              defaultActiveFirstOption
              options={measuresList ?? []}
              fieldNames={{ value: 'id', label: 'name' }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Space.Compact>
      </Badge.Ribbon>
    </Flex>
  );
};

type FormData = MealItem & {
  newMealItem: boolean;
};

const MealItemForm = (props: MealItemFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm<FormData>();
  const { token } = theme.useToken();
  const { mealItem, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!mealItem.id, [mealItem.id]);
  const initialValues = useMemo(
    () => ({
      servingTool: null,
      servingSize: 1.0,
      size: 1.0,
      ...mealItem,
      mealItemIngredients: mealItem.mealItemIngredients?.toSorted(
        (a, b) => Number(b.isPrimary) - Number(a.isPrimary)
      ) ?? [new MealItemIngredient(undefined, undefined, 1, undefined, undefined, 0)],
      newMealItem: false,
    }),
    [mealItem]
  );
  const ingredients = useQuery<Ingredient[]>([
    'ingredient',
    {
      page: { limit: 10000 },
      sort: 'name',
      fields: {
        ingredient: ['name'],
      },
    },
  ]);
  const measures = useQuery<Measure[]>([
    'measure',
    {
      page: {
        limit: 10000,
      },
      sort: 'name',
      fields: {
        measure: 'name',
      },
    },
  ]);
  const servingTools = useQuery<ServingTool[]>([
    'serving-tool',
    {
      page: {
        totals: 1,
        number: 1,
        size: 10000,
      },
      sort: 'name',
      fields: {
        'serving-tool': ['name'],
      },
    },
  ]);
  const atomicOperations = useAtomicOperations();

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues, servingTools.data, measures.data, ingredients.data]);

  const onSubmit = (submitted: FormData): void => {
    const mealItemToBeSaved = new MealItem(
      submitted.id,
      submitted.name,
      new Measure(submitted.servingMeasure.id),
      new Measure(submitted.measure.id),
      submitted.servingSize,
      [],
      submitted.servingTool?.id ? new ServingTool(submitted.servingTool.id) : null,
      submitted.size
    );

    // @ts-expect-error operand of delete operator must be optional
    delete mealItemToBeSaved.mealItemIngredients;

    const mealItemOperation: AtomicOperation<MealItem> = isEditMode
      ? AtomicOperation.update(mealItemToBeSaved)
      : AtomicOperation.add(mealItemToBeSaved, randomUUID());

    const miis = submitted.mealItemIngredients.map(
      (mii, idx) =>
        new MealItemIngredient(
          mii.id,
          new Ingredient(mii.ingredient.id),
          mii.units,
          mii.measure,
          new MealItem(mealItemToBeSaved.id ?? mealItemOperation.lid),
          idx
        )
    );
    const existingMiisIds = mealItem.mealItemIngredients?.map((mii) => mii.id) ?? [];
    const newMiisIds = miis.map((mii) => mii.id) ?? [];

    const mealItemIngredientsToRemove =
      mealItem.mealItemIngredients
        ?.filter((mii) => mii.id && !newMiisIds.includes(mii.id))
        .map((mii) =>
          AtomicOperation.remove<MealItemIngredient>([
            'meal-item',
            mii.mealItem.id!,
            'mealItemIngredients',
            mii.id!,
          ])
        ) ?? [];
    const mealItemIngredientsToUpdate =
      miis
        .filter((mii) => mii.id && existingMiisIds.includes(mii.id))
        .map((mii) =>
          AtomicOperation.update<MealItemIngredient>(mii, undefined, [
            'meal-item',
            mii.mealItem.id!,
            'mealItemIngredients',
            mii.id!,
          ])
        ) ?? [];
    const mealItemIngredientsToAdd = miis
      .filter((mii) => !mii.id)
      .map((mii) =>
        AtomicOperation.add<MealItemIngredient>(mii, undefined, [
          'meal-item',
          mii.mealItem.id!,
          'mealItemIngredients',
          mii.id!,
        ])
      );

    atomicOperations
      .operate(
        Array.of<AtomicOperation<MealItem | MealItemIngredient>>(
          mealItemOperation,
          ...mealItemIngredientsToRemove,
          ...mealItemIngredientsToUpdate,
          ...mealItemIngredientsToAdd
        )
      )
      .pipe(
        switchMap(([mi, ...miis]) =>
          of<MealItem>({
            ...(mi.data as MealItem),
            mealItemIngredients: miis
              .map((mii) => mii.data as MealItemIngredient)
              .filter((mii) => mii),
          })
        ),
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap((mi) => {
          console.warn(submitted.newMealItem);
          onSuccess(mi, submitted.newMealItem);
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="meal-item-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item<FormData>>
            <Typography.Title level={4}>
              {isEditMode ? mealItem.name : 'New Meal Item'}
            </Typography.Title>
          </Form.Item>

          <Form.Item<FormData> name="newMealItem" valuePropName="checked" hidden>
            <Checkbox />
          </Form.Item>

          <Form.Item<FormData> name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item<FormData> label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Space.Compact style={{ flex: '1 1 100%', display: 'flex' }}>
            <Form.Item<FormData>
              label="Serving size"
              name="servingSize"
              rules={[{ required: true, type: 'number', min: 0.01 }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item<FormData>
              label=" "
              name={['servingMeasure', 'id']}
              rules={[{ required: true, message: REQUIRED_SERVING_MEASURE_MESSAGE }]}
              style={{ flex: 1 }}
            >
              <Select
                showSearch
                optionFilterProp="name"
                defaultActiveFirstOption
                options={measures.data ?? []}
                fieldNames={{ label: 'name', value: 'id' }}
                disabled={measures.isLoading}
                loading={measures.isLoading}
              />
            </Form.Item>
          </Space.Compact>
          <Space.Compact style={{ flex: '1 1 100%', display: 'flex' }}>
            <Form.Item<FormData>
              label="Measure"
              name="size"
              rules={[{ required: true, type: 'number', min: 0.01 }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item<FormData>
              label=" "
              name={['measure', 'id']}
              rules={[{ required: true, message: REQUIRED_MEASURE_MESSAGE }]}
              style={{ flex: 1 }}
            >
              <Select
                showSearch
                optionFilterProp="name"
                defaultActiveFirstOption
                options={measures.data ?? []}
                fieldNames={{ label: 'name', value: 'id' }}
                disabled={measures.isLoading}
                loading={measures.isLoading}
              />
            </Form.Item>
          </Space.Compact>

          <Form.Item<FormData>
            label="Serving Tool"
            name={['servingTool', 'id']}
            style={{ flex: 1 }}
          >
            <Select
              showSearch
              optionFilterProp="name"
              placeholder="N/A"
              options={servingTools.data ?? []}
              fieldNames={{ value: 'id', label: 'name' }}
              allowClear
              disabled={servingTools.isLoading}
              loading={servingTools.isLoading}
            />
          </Form.Item>
          <Divider orientation="left">Ingredients</Divider>

          <SyncSuspense
            fallback={
              <Flex gap="small" style={{ marginBlock: token.marginMD }} vertical>
                <Skeleton.Input block size="small" active />
                <Skeleton.Input block size="small" active />
                <Skeleton.Input block size="small" active />
              </Flex>
            }
            condition={!ingredients.isLoading}
          >
            <IngredientsListContext.Provider value={ingredients.data ?? []}>
              <MeasuresListContext.Provider value={measures.data ?? []}>
                <Form.List
                  name="mealItemIngredients"
                  rules={[
                    {
                      validator: (_rule, value, callback) => {
                        callback(
                          Array.isArray(value) && value.length > 0
                            ? undefined
                            : 'At least one ingredient is required'
                        );
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <Flex gap="middle" vertical>
                      {fields.map(({ key, name }) => (
                        <MealItemIngredientSelector
                          key={key}
                          fieldIdx={name}
                          onRemove={() => {
                            remove(name);
                          }}
                        />
                      ))}

                      <Form.ErrorList errors={errors} helpStatus="error" />

                      <Form.Item<FormData>>
                        <Button
                          block
                          type="dashed"
                          onClick={() => {
                            add(new MealItemIngredient());
                          }}
                        >
                          Add ingredient
                        </Button>
                      </Form.Item>
                    </Flex>
                  )}
                </Form.List>
              </MeasuresListContext.Provider>
            </IngredientsListContext.Provider>
          </SyncSuspense>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse', marginBlock: token.marginMD }}>
              <Dropdown
                menu={{
                  items: [
                    {
                      label: `Save`,
                      key: '1',
                      onClick: () => {
                        formInstance.setFieldValue(['newMealItem'], false);
                        formInstance.submit();
                      },
                    },
                    {
                      label: `Save and Create new one`,
                      key: '2',
                      onClick: () => {
                        formInstance.setFieldValue(['newMealItem'], true);
                        formInstance.submit();
                      },
                    },
                  ],
                }}
              >
                <Button
                  block
                  icon={<ChevronDown />}
                  type="primary"
                  disabled={ingredients.isLoading || atomicOperations.isLoading}
                  loading={atomicOperations.isLoading}
                >
                  Save
                </Button>
              </Dropdown>
              <Button block onClick={onCancel} disabled={atomicOperations.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default MealItemForm;
